import React from 'react';
import { Link } from '@tanstack/react-router';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { formatCalendarDate, formatMoney } from '@/utils/formatters';
import type { RenewalForecastRow } from './forecastRows';

interface RenewalForecastTableProps {
  rows: RenewalForecastRow[];
  /** Adds a Period column (the full Renewals page lists several periods at once) */
  showPeriod?: boolean;
  emptyMessage?: string;
  testId?: string;
}

/**
 * The contracts of one or more forecast periods: contract (linked), vendor, date and its source,
 * amount in the contract's own currency, auto-renew badge, owner. Plain table, no paging: a
 * forecast window holds at most a few dozen rows.
 */
export const RenewalForecastTable: React.FC<RenewalForecastTableProps> = ({
  rows,
  showPeriod = false,
  emptyMessage = 'No contracts in this period',
  testId = 'renewal-forecast-table',
}) => (
  // contain-inline-size: the table scrolls inside this wrapper instead of widening the page at phone width
  <div className="overflow-x-auto contain-inline-size" data-testid={testId}>
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Contract</TableHead>
          <TableHead>Vendor</TableHead>
          {showPeriod && <TableHead>Period</TableHead>}
          <TableHead>Date</TableHead>
          <TableHead className="text-right">Amount</TableHead>
          <TableHead>Renewal</TableHead>
          <TableHead>Owner</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {rows.length === 0 ? (
          <TableRow>
            <TableCell colSpan={showPeriod ? 7 : 6} className="text-center text-grey-500 py-6">
              {emptyMessage}
            </TableCell>
          </TableRow>
        ) : (
          rows.map((row) => (
            <TableRow key={row.contractId}>
              <TableCell>
                <Link
                  to="/contracts/$id"
                  params={{ id: row.contractId }}
                  className="font-medium text-primary hover:underline"
                >
                  {row.contractNumber}
                </Link>
                <span className="block text-xs text-grey-500 break-words">{row.title}</span>
              </TableCell>
              <TableCell className="break-words">{row.vendorName}</TableCell>
              {showPeriod && <TableCell className="whitespace-nowrap">{row.periodLabel}</TableCell>}
              <TableCell className="whitespace-nowrap">
                {formatCalendarDate(row.date)}
                <span className="block text-xs text-grey-500">
                  {row.dateSource === 'RenewalDate' ? 'renewal date' : 'end date'}
                </span>
              </TableCell>
              <TableCell className="text-right whitespace-nowrap tabular-nums">
                {formatMoney(row.amount, row.currency)}
              </TableCell>
              <TableCell>
                {row.autoRenews ? (
                  <Badge variant="info">Auto-renews</Badge>
                ) : (
                  <Badge variant="warning">Decision needed</Badge>
                )}
              </TableCell>
              <TableCell className="break-words">{row.ownerName ?? '—'}</TableCell>
            </TableRow>
          ))
        )}
      </TableBody>
    </Table>
  </div>
);
